/**
 * Global axios configuration and interceptors.
 *
 * Phase 2 hardening: registers a global response interceptor that
 * detects 401 responses, clears authentication state, and redirects
 * to /login. This prevents users from seeing broken pages when
 * sessions expire mid-use.
 *
 * Imported once in App.js so the interceptor registers on app boot.
 *
 * Coexistence with local 401 handlers:
 *   Some components (TenantAdminDashboard, TenantDetail, TenantAuditLogs)
 *   have their own 401 handlers. The interceptor re-throws via
 *   Promise.reject(error) so local catch blocks still run.
 */

import axios from 'axios';
import { clearAuth } from '../utils/auth';

axios.interceptors.response.use(
  response => response,
  error => {
    // WAITLIST (2026-10-19): backend returns 403 {detail:{code:'ACCOUNT_PENDING'}} for
    // pending accounts on every gated endpoint. Send them to the waitlist page once.
    if (error.response?.status === 403 && error.response?.data?.detail?.code === 'ACCOUNT_PENDING') {
      if (!window.location.pathname.startsWith('/waitlist')) {
        window.location.href = '/waitlist';
      }
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      // Avoid redirect loop if already on /login
      if (!currentPath.startsWith('/login')) {
        console.log('[Auth] 401 detected - clearing auth and redirecting to login');
        clearAuth();
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
